import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, switchMap } from 'rxjs';
import { CFSectionToDisplay } from 'src/app/models/cf-document/cf-section-to-display';
import { CFParticipantToDisplay } from 'src/app/models/participants/cf-participant-to-display';
import { AuthenticationService } from '../../auth/authentication.service';
import { AuthenticatedUser } from '../../models/auth/authenticated-user.model';
import { SecuredSessionEncodedData } from '../../models/auth/secured-session-encoded-data.model';
import { CFDocumentToDisplay } from '../../models/cf-document/cf-document-to-display';
import { KeyValuePairEnum } from '../../models/key-value-pair-enum';
import { CustomNotificationService } from '../../services/custom-notification.service';
import { CFDocumentStoreService } from '../../services/documents/cf-document-store.service';
import { CFDocumentService } from '../../services/documents/cf-document.service';
import { CFSignatureStoreService } from '../../services/signatures/cf-signature-store.service';
import { CustomMessagesService } from '../../services/translation/custom-messages.service';
import { base64toJson } from '../../util/utils';

@UntilDestroy()
@Component({
    selector: 'clfl-document-secured-session',
    templateUrl: './cf-document-secured-session.component.html',
    styleUrls: ['./cf-document-secured-session.component.scss']
})
export class CfDocumentSecuredSessionComponent implements OnInit, OnDestroy {
    public document!: CFDocumentToDisplay;
    public documentTypes!: Array<KeyValuePairEnum>;
    public authenticatedUser: AuthenticatedUser;
    public title: string;

    public isLoading = false;
    public isDocumentLoaded = false;
    public allowSigning = false;
    public isSignWithPinDialogOpened = false;
    public isSignOnGlassDialogOpened = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public authenticationService: AuthenticationService,
        private notificationService: CustomNotificationService,
        private msgService: CustomMessagesService,
        private documentService: CFDocumentService,
        public documentStoreService: CFDocumentStoreService,
        public signatureStoreService: CFSignatureStoreService
    ) {}

    ngOnInit(): void {
        this.documentStoreService.documentTypes$.pipe(untilDestroyed(this)).subscribe({
            next: (types) => (this.documentTypes = types)
        });
        this.isLoading = true;
        this.isDocumentLoaded = false;

        this.msgService.sharedLanguage.pipe(untilDestroyed(this)).subscribe((language) => {
            this.setTitle();
        });

        this.route.data
            .pipe(
                untilDestroyed(this),
                switchMap((data: { encodedSessionData: SecuredSessionEncodedData }) => {
                    this.authenticatedUser = base64toJson(data.encodedSessionData.jwt) as AuthenticatedUser;

                    this.authenticationService.setLoggedInUser(this.authenticatedUser);

                    this.signatureStoreService.selectedSectionId = this.authenticatedUser.userResource.sectionId;

                    this.setTitle();

                    const document$ = this.documentService.getDocumentById(this.documentStoreService.cloudFlowDocumentId);

                    return forkJoin([document$]);
                })
            )
            .subscribe({
                next: (response) => {
                    if (response && response.length > 0) {
                        this.onDocumentFound(response[0]);
                    }
                },
                error: (err) => {
                    this.router.navigate(['/login']);
                }
            });
    }

    public setTitle() {
        this.title =
            this.authenticatedUser && this.authenticatedUser.userResource.canSign
                ? this.msgService.translate('cf-document-anonymous.label.sign-title')
                : this.msgService.translate('cf-document-anonymous.label.view-title');
    }

    public onSearch() {
        this.isLoading = true;
        this.isDocumentLoaded = false;

        this.documentService
            .getDocumentById(this.documentStoreService.cloudFlowDocumentId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.onDocumentFound(response);
                    }
                }
            });
    }

    onDocumentFound(cfDocument: CFDocumentToDisplay) {
        this.isDocumentLoaded = true;
        this.isLoading = false;
        this.document = cfDocument;

        const sectionAboutToSign = this.document.sections.find(
            (section) => section.sectionId === this.signatureStoreService.selectedSectionId
        );

        this.documentStoreService.setDocumentData(this.document);

        this.documentStoreService.selectedDocumentType = this.documentTypes.find((d) => d.value === this.document.documentType).key;

        this.documentStoreService.documentNumber = this.document.documentNumber;
        this.documentStoreService.companyId = this.document.companyId;

        this.allowSigning =
            !sectionAboutToSign.isSigned &&
            this.authenticationService.currentUserValue.userResource.canSign &&
            sectionAboutToSign.sectionId === this.authenticationService.currentUserValue.userResource.sectionId;

        this.notificationService.success(this.msgService.translate('cf-document.notification.documentFound'));
    }

    public setLoadingState(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    public openSignDialog() {
        switch (this.signatureStoreService.selectedSignatureType) {
            case 0:
                this.isSignWithPinDialogOpened = true;
                break;
            case 1:
                this.isSignOnGlassDialogOpened = true;
                break;
            default:
                this.isSignWithPinDialogOpened = true;
                break;
        }
    }

    public getUnsignedSection(): Array<CFSectionToDisplay> {
        return [
            this.document.sections.find(
                (section) => !section.isSigned && section.sectionId === this.signatureStoreService.selectedSectionId
            )
        ];
    }

    public getSessionParticipant(): Array<CFParticipantToDisplay> {
        let list = this.document.participants.find(
            (participant) =>
                participant.participantId === this.signatureStoreService.selectedSignerId &&
                participant.sectionId === this.signatureStoreService.selectedSectionId
        );

        if (list == undefined) {
            list = this.document.participants.find((participant) => participant.sectionId == this.signatureStoreService.selectedSectionId);
        }

        return [list];

    }

    ngOnDestroy(): void {
        this.authenticationService.logout();
    }
}
